/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBottomActionScroll } from '@euler/functions/useBottomActionScroll';
import { useTask } from '@euler/functions/useTask';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import { CustomerType, SiteInspectionType } from '@euler/model/enum';
import { formatDate, formatDateTime } from '@euler/utils/formatters';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { displayChassisNo } from '@euler/utils/vin/displayChassisNo';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useCallback, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  TouchableOpacity,
  findNodeHandle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  formatCarwashType,
  formatOldPartDisposableType,
  formatReservationType,
  formatReturnType,
} from '../../../../../functions/order/enumValueUtil';
import { useChangeOrderFlowStep } from '../../functions/useChangeOrderFlowStep';
import { vehicleIdentityFromForm } from '../../functions/vinHelper';
import { useCreateOrder } from '../OrderScreen/functions/useCreateOrder';
import { OrderInfoCell as Cell } from './components/Cell';
import { CustomerSignSection } from './components/CustomerSignSection';
import { DashboardInfoSection } from './components/DashboardInfoSection';
import { ExtendedPropertiesCells } from './components/ExtendedPropertiesCells';
import { FacadeInfoSection } from './components/FacadeInfoSection';
import { PreviewOrderNavHeader } from './components/Header';
import { OrderPreviewSection as Section } from './components/Section';
import { ServiceSubjectSection } from './components/ServiceSubjectSection';
import { TermsOfServiceSection } from './components/TermsOfServiceSection';
import { VehicleSubscriptionQrcode } from './components/VehicleSubscriptionQrcode';
import { useValidateFacadeSiteRequirements } from './functions/useValidateFacadeSiteRequirements';

export const OrderPreviewScreen = wrapNavigatorScreen(
  () => {
    const {
      formController: { form },
      orderDetail$,
      storeConfiguration,
      inventoryQuery,
    } = useOrderFlowContext();

    useChangeOrderFlowStep('preview');

    const { bottom } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { scrollY, onScroll } = useBottomActionScroll();
    const [detail] = useBehaviorSubject(orderDetail$);
    const [showQrcodeTip, setShowQrcodeTip] = useState(true);
    const submitButtonRef = useRef<TouchableOpacity>(null);

    const { showActionSheetWithOptions } = useActionSheet();

    const createOrder = useCreateOrder();
    const validateFacadeSiteRequirements = useValidateFacadeSiteRequirements();

    const inquire = usePersistFn(async () => {
      // if there are no facade inspections, never finish pre-inspection.
      if (
        !detail.preInspection?.filter(
          x => x.inspectionType === SiteInspectionType.Facade,
        ).length
      ) {
        AnalyticEvents.default.orderSubmitWithoutPreInspection();
        return false;
      }

      return await new Promise<boolean | undefined>(resolve => {
        const nodeId = submitButtonRef.current
          ? findNodeHandle(submitButtonRef.current)
          : undefined;
        showActionSheetWithOptions(
          {
            title: '确认建单',
            message: '是否在建单完成后推送预检报告?',
            destructiveButtonIndex: 0,
            cancelButtonIndex: 2,
            cancelButtonTintColor: '#999',
            anchor: nodeId ?? undefined,
            options: [
              '完工建单并推送预检报告',
              '仅建单，稍后推送预检报告',
              '关闭',
            ],
          },
          buttonIndex => {
            if (buttonIndex === 0) {
              // pushing the report finishes the pre-inspection, so it has to
              // clear the facade template's required sites first. creating the
              // order without pushing stays available on a second attempt.
              if (!validateFacadeSiteRequirements()) {
                resolve(undefined);
                return;
              }
              resolve(true);
              AnalyticEvents.default.orderSubmitWithFinishPreInspection();
            } else if (buttonIndex === 1) {
              resolve(false);
              AnalyticEvents.default.orderSubmitWithoutFinishPreInspection();
            } else {
              resolve(undefined);
            }
          },
        );
      });
    });

    const submit = useTask(async (shouldFinishPreInspection: boolean) => {
      await createOrder({ shouldFinishPreInspection });
    });

    const onSubmit = usePersistFn(async () => {
      const shouldFinishPreInspection = await inquire();
      if (shouldFinishPreInspection == null) return;
      await submit.request(shouldFinishPreInspection);
    });

    const onQrcodeErrorLoad = useCallback(() => {
      setShowQrcodeTip(false);
    }, []);

    const chassisNo = displayChassisNo(
      vehicleIdentityFromForm({
        isNonStandardVin: form.isNonStandardVin.value,
        vin: form.vin.value,
        chassisNo: form.chassisNo.value,
      }),
    );

    return (
      <LayoutProviderView
        css={`
          flex: 1;
          background-color: #fff;
        `}
      >
        <PreviewOrderNavHeader
          licensePlateNo={form.licensePlateNo.value}
          mileage={form.mileage.value}
          vehicleName={form.vehicleInfo.value?.vehicleName ?? ''}
          vehicleImageUrl={form.vehicleInfo.value?.vehicleImageUrl}
        />

        <ScrollView
          scrollEventThrottle={1}
          onScroll={onScroll}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{
            paddingTop: rpx(10),
            paddingBottom: bottom + rpx(64),
          }}
        >
          <Section
            title="服务信息"
            headerRight={
              showQrcodeTip ? (
                <HStack justifyContent="flex-end">
                  <Icon
                    name="information-solid"
                    size={rpx(16)}
                    color={StatusColors.Info}
                  />
                  <Typography.Label
                    medium
                    size={rpx(15)}
                    color={StatusColors.Info}
                    lineHeight={rpx(20)}
                    css={`
                      margin-left: ${rpx(4)}px;
                    `}
                  >
                    扫码关注
                  </Typography.Label>
                </HStack>
              ) : undefined
            }
          >
            <VehicleSubscriptionQrcode
              licensePlateNo={form.licensePlateNo.value}
              onError={onQrcodeErrorLoad}
              css={`
                margin: ${rpx(15)}px 0;
              `}
            />
            <Cell title="业务类型" detail={form.tags.value?.join(',')} />
            {chassisNo !== '' && <Cell title="车架号" detail={chassisNo} />}
            {form.mileage.value > 0 && (
              <Cell title="进店里程" detail={`${form.mileage.value}公里`} />
            )}
            <Cell title="服务顾问" detail={form.serviceAgentInfo.value?.name} />
            <Cell
              title="取车前是否洗车"
              detail={formatCarwashType(form.carwashType.value)}
            />
            <Cell
              title="服务是否预约"
              detail={formatReservationType(form.reservationType.value)}
            />
            <Cell
              title="保险到期日期"
              detail={
                form.insuranceExpiryDate.value != null
                  ? formatDate(form.insuranceExpiryDate.value)
                  : '无'
              }
            />
            <Cell
              title="维修旧件处理方式"
              detail={formatOldPartDisposableType(
                form.oldPartDisposalType.value,
              )}
            />
            <Cell
              title="车辆归还方式"
              detail={formatReturnType(form.returnType.value)}
            />
            <Cell
              title="预计交车时间"
              detail={
                form.estimatedFinishTime.value != null
                  ? formatDateTime(form.estimatedFinishTime.value)
                  : '无'
              }
            />
            <ExtendedPropertiesCells
              storeConfiguration={storeConfiguration}
              detail={detail}
            />
            <Cell title="服务备注" detail={form.remark.value || '无'} last />
          </Section>

          <ServiceSubjectSection serviceSubjects={detail.serviceSubjects} />

          <Section title="客户信息">
            <Cell
              title="客户类型"
              detail={
                form.customerType.value === CustomerType.Default
                  ? '私家车'
                  : form.customerType.value === CustomerType.Official
                  ? '公务/单位车'
                  : '无'
              }
            />
            <Cell title="客户姓名" detail={form.customerName.value || '无'} />
            <Cell
              title="客户电话"
              detail={form.customerMobile.value || '无'}
              last
            />
          </Section>

          <DashboardInfoSection
            inventoryQuery={inventoryQuery}
            detail={detail}
            dashboardImgUrl={form.dashboardImgUrl.value}
          />

          <FacadeInfoSection detail={detail} />

          <TermsOfServiceSection storeConfiguration={storeConfiguration} />

          <CustomerSignSection signImageUrl={detail.signImageUrl} />
        </ScrollView>

        <BottomAction scrollY={scrollY}>
          <BottomAction.Button
            text="确认建单"
            variant="primary"
            loading={submit.loading}
            buttonRef={submitButtonRef}
            onPress={onSubmit}
            disabled={submit.loading}
          />
        </BottomAction>
      </LayoutProviderView>
    );
  },
  {
    title: '确认订单信息',
    headerShown: false,
  },
);
